import React, {
  useEffect,
  useState,
  ComponentType,
  SetStateAction
} from 'react';
import { wplToken } from '@atlassiansox/wpl-token';
import { css } from '@emotion/react';
import { getColor } from '../foundations';

interface TextInputProps {
  id: string;
  name: string;
  placeholder: string;
  value?: string;
  label: string;
  disabled?: boolean;
  testId?: string;
  validation?: (arg0: string) => [boolean, string];
  Icon?: ComponentType;
  onChangeHandler?: React.Dispatch<SetStateAction<string>>;
  className?: string;
  autocomplete?: string | undefined;
}

const containerStyles = css`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const labelStyles = css`
  color: ${getColor('n700')};
  font-size: 0.75rem;
  line-height: 1.1;
  margin-bottom: ${wplToken('space.050')};
  text-align: left;
`;

const inputContainerStyles = css`
  position: relative;
`;

const inputStyles = (isValid: boolean) => css`
  box-sizing: border-box;
  background-color: ${getColor('n10')};
  border: 2px solid #dfe1e6;
  border-radius: 4px;
  flex-shrink: 0;
  height: 2.65rem;
  padding-left: 8px;
  width: 100%;
  font-size: 1rem;
  line-height: 1.2;
  text-align: left;
  color: ${getColor('n900')};

  &::placeholder {
    color: ${getColor('n70')};
  }

  &:hover {
    border-radius: 4px;
    border: 2px solid #dfe1e6;
    background: ${getColor('n30')};
  }

  &:disabled {
    border-radius: 4px;
    border: 2px solid #dfe1e6;
    background: ${getColor('n20')};
    color: ${getColor('n70')};
  }

  &:focus {
    outline: 1px solid transparent;
    ${isValid
      ? `border: 2px solid ${getColor('b100')};`
      : `border: 2px solid ${getColor('r400')};`};
  }
`;

const errorStyles = css`
  color: ${getColor('r400')};
  font-size: 0.75rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.2;
  margin: 0;
  margin-top: 4px;
`;

const iconStyles = css`
  position: absolute;
  margin: auto;
  top: 0;
  bottom: 0;
  right: 10px;
  width: 24px;
  height: 24px;
  pointer-events: none;
`;

const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (props, ref) => {
    const {
      name,
      value,
      id,
      testId,
      label,
      placeholder,
      disabled,
      validation,
      Icon,
      onChangeHandler,
      className,
      autocomplete
    } = props;

    const [text, setText] = useState('');
    const [isValid, setValid] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
      if (text && validation) {
        const { 0: valid, 1: error } = validation(text);
        if (valid) {
          setError('');
        } else {
          setError(error);
        }
        setValid(valid);
      }

      if (!text) {
        setError('');
      }
    }, [text]);

    return (
      <div className={className} css={containerStyles}>
        <label css={labelStyles} htmlFor={name}>
          {label}
        </label>
        <div className="input-container" css={inputContainerStyles}>
          <input
            onChange={(e) => {
              setText(e.target.value);
              onChangeHandler && onChangeHandler(e.target.value);
            }}
            css={inputStyles(isValid)}
            type="text"
            ref={ref}
            name={name}
            id={id}
            data-testid={testId ? testId : id}
            value={value}
            placeholder={placeholder}
            disabled={disabled || false}
            aria-invalid={!!error}
            aria-errormessage={!!error ? `err${id}` : ''}
            autoComplete={autocomplete}
          />
          {Icon && (
            <div css={iconStyles}>
              <Icon />
            </div>
          )}
        </div>
        {!!error && (
          <label id={`err${id}`} css={errorStyles}>
            {error}
          </label>
        )}
      </div>
    );
  }
);

TextInput.displayName = 'TextInput';

export { TextInput };
